import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, CircularProgress, Divider, FormLabel } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import axios from 'axios';
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomAutocomplete from "../../common/CustomAutoComplete";
import { UPDATE_IMPORT_SHIPPING_MARK, READ_IMPORT_SHIPPING_MARK } from '../../../services/apiRoutes';
import CustomStyle from "../../common/CustomStyle";
import { Editor } from "react-draft-wysiwyg";
import { stateToHTML } from "draft-js-export-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from 'draftjs-to-html';
import { EditorState, convertFromHTML, ContentState, convertToRaw } from "draft-js";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    batchNo: Yup.string().required('Batch Number is required'),
    productId: Yup.number().required('Product is required'),
});


const UpdateExpShippingMark = ({ handleClose, setMessage, fetchData, shippingMarkId, productsList }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const onEditorStateChange = (state) => {
        setEditorState(state);
    };
    const convertToHTMLContent = () => {
        const contentState = editorState.getCurrentContent();
        // const htmlContent = stateToHTML(contentState);
        const htmlContent = draftToHtml(convertToRaw(contentState));
        return htmlContent;
    };


    useEffect(() => {
        if (!shippingMarkId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_IMPORT_SHIPPING_MARK(shippingMarkId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setData(response.data.shipment);
                const htmlContent = response.data.shipment.shippingMark || '';
                const contentState = htmlContent ? convertFromHTML(htmlContent) : null;
                setEditorState(EditorState.createWithContent(ContentState.createFromBlockArray(contentState.contentBlocks)));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [shippingMarkId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_IMPORT_SHIPPING_MARK(shippingMarkId), {
                ...values,
                shippingMark: convertToHTMLContent(),
            }, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Shipping Mark updated successfully",
                    type: "success",
                });
                fetchData();
                setTimeout(() => {
                    handleClose();
                },);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    batchNo: data?.batchNo || '',
                    productId: data?.productId || '',
                    shippingMark: data?.shippingMark || '',
                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update Shipping Mark")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title=" Save and New"
                                        size="small"
                                        variant="contained"
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>


                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                <CustomTextField
                                    required={true}
                                    name="batchNo"
                                    fieldLabel="Batch Number"
                                    value={values.batchNo}
                                    onChange={handleChange}
                                    error={touched.batchNo && !!errors.batchNo}
                                    helperText={touched.batchNo && errors.batchNo}
                                    placeholder="Enter Batch Number"
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                <CustomAutocomplete
                                    options={productsList}
                                    fieldLabel="Select Product"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={productsList.find((option) => option.id === values.productId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('productId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Product"
                                    error={touched.productId && !!errors.productId}
                                    helperText={touched.productId && errors.productId}
                                />
                            </Grid>

                            <Grid item xs={12} md={12}>
                                <FormLabel sx={{ paddingBottom: "10px" }} style={fieldLabelstyle}>{t("Shipping Mark")}</FormLabel>
                                <Editor
                                    editorState={editorState}
                                    onEditorStateChange={onEditorStateChange}
                                    wrapperClassName="demo-wrapper"
                                    editorClassName="demo-editor"
                                    wrapperStyle={{
                                        border: '1px solid #CED4DA', borderRadius: '4px',
                                    }}
                                    editorStyle={{ padding: "10px" }}
                                    toolbar={{
                                        options: [
                                            'inline',
                                            'blockType',
                                            'fontSize',
                                            'fontFamily',
                                            'list',
                                            'textAlign',
                                            'colorPicker',
                                            'link',
                                            'emoji',
                                            'remove',
                                            'history'
                                        ],
                                        inline: {
                                            inDropdown: false,
                                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace', 'superscript', 'subscript'],
                                        },
                                        blockType: {
                                            inDropdown: true,
                                            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                                        },
                                        fontSize: {
                                            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                                        },
                                        list: {
                                            inDropdown: false,
                                            options: ['unordered', 'ordered', 'indent', 'outdent'],
                                        },
                                        textAlign: {
                                            inDropdown: false,
                                            options: ['left', 'center', 'right', 'justify'],
                                        },
                                        link: {
                                            inDropdown: false,
                                            options: ['link', 'unlink'],
                                        },
                                        history: {
                                            inDropdown: false,
                                            options: ['undo', 'redo'],
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateExpShippingMark;
const fieldLabelstyle = {
    color: "#212529",
    fontWeight: "600",
    fontSize: "14px",
};